import * as z from 'zod'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { isTextablePhone } from '@/lib/phone'
import { isCompletePostalCode } from '@/lib/address'

// Quick-capture's contract: WHO/WHERE (one answer now — the address, presented by
// its customer), and WHAT. Everything the old form asked on top of those was
// either derivable (the title), postponable to the job page (crew, people,
// dispatchers, row surgery) or a default wearing a control — a phone call does not
// have time for any of it.
export function useSchemaJob() {
  const { t } = useI18n()
  return computed(() =>
    z.object({
      // WHO + WHERE, in ONE field. A job points at a service_location and always
      // did; the contact was never the object, only the door the old form gave you
      // to a location — which is why a company-owned address was unbuildable.
      // '' = unanswered (submit blocked), an id = picked or prefilled,
      // '__new__' = the inline new-address block is open.
      serviceLocationId: z.string().default(''),
      // WHAT — REQUIRED, on both lanes. A caller names the work («come see my
      // roof»), so an optional type was designed around a caller who doesn't
      // exist, and the type is consumed long before anyone is on site: the
      // evaluation SLOT times the day, the quote lane routes it to a crew, the
      // lenses hide what has no type, and signature seeds the program from it.
      // The combobox can create one in place, so requiring it never dead-ends
      // the call.
      //
      // Client-side only: the API stays permissive (imports, legacy rows, the
      // untyped fixtures that exercise « Sans type »), and the server-side gate
      // arrives with Slice C's signature-seeding.
      templateId:        z.string().min(1, { message: t('validation.required', { field: t('jobs.fields.jobType') }) }),
      // « La suite » — the deal's state at the end of the call (design 165),
      // mirrored from useJobIntake's ref so the submit carries it.
      intakeLane:        z.enum(['lead', 'evaluation', 'quote', 'won']).default('evaluation'),
      // « Combien ? » — the « Vendu » card's money question, the three-way
      // radio (CommitmentFields): « Soumission type » (auto-selected when the
      // picked type's template can answer), « Montant convenu » (the custom
      // line — label/taxable/kind/description are the line's own), « Sans
      // soumission » (the explicit skip_proposal escape). NO default: the
      // question never resolves by silence — the refine below blocks an
      // unanswered « Vendu » submit, and no other card reads these.
      agreementMode:     z.enum(['template', 'priced', 'no_proposal']).optional(),
      agreedAmount:      z.number().optional(),
      agreedLabel:       z.string().optional().default(''),
      agreedTaxable:     z.boolean().default(true),
      agreedKind:        z.enum(['material', 'labor', 'fee']).default('labor'),
      agreedDescription: z.string().optional().default(''),
      // The new address and its customer, said in one breath on the phone.
      // Present only while serviceLocationId === '__new__'. Shapes are the
      // SHARED builders below — the évaluation door mounts the same block.
      newCustomer:       buildNewCustomerSchema(t).optional(),
      newAddress:        buildNewAddressSchema(t).optional(),
    }).superRefine((val, ctx) => {
      // A won deal names its price or says « Sans soumission » — unanswered,
      // or priced without its amount, blocks the submit. (The template face's
      // completeness is the mini form's own gate, in the form's CTA.)
      if (val.intakeLane === 'won'
          && (val.agreementMode === undefined
            || (val.agreementMode === 'priced'
              && !(typeof val.agreedAmount === 'number' && val.agreedAmount > 0)))) {
        ctx.addIssue({
          code: 'custom',
          path: ['agreedAmount'],
          message: t('jobs.agreement.amountRequired'),
        })
      }

      const creating = val.serviceLocationId === '__new__'

      if (!creating) {
        // The address IS the answer to who-and-where; nothing else stands in for
        // it. A blank here would send a crew to no door at all.
        if (!val.serviceLocationId) {
          ctx.addIssue({
            code: 'custom',
            path: ['serviceLocationId'],
            message: t('validation.required', { field: t('customers.customer') }),
          })
        }
        return
      }

      const customer = val.newCustomer
      if (!customer) return

      refineInlineCustomer(customer, ctx, t)
    })
  )
}

// ── The inline-customer block's SHARED pieces ────────────────────────────────
// One spelling for the capture forms AND the évaluation door (which mounts the
// same CustomerCreateBlock): the shapes, and the refine that guards them.
type TFn = (key: string, named?: Record<string, unknown>) => string

// `kind` is an EXPLICIT segmented choice, never an empty-field default: the
// dispatcher says whether they are talking to a person or a company, and that
// decision changes which record is born.
// ⭐ On the `company` branch the personne-ressource is OFFERED, NOT REQUIRED
// (owner's ruling): no person simply means this customer cannot be texted, and
// a required field there either stalls the call or mints a placeholder that
// fails SMS silently and poisons the dedup slice.
export function buildNewCustomerSchema(t: TFn) {
  return z.object({
    kind:          z.enum(['contact', 'company']).default('contact'),
    // Person fields — the customer on the `contact` branch, the
    // personne-ressource on the `company` branch. Same shape either way.
    firstName:     z.string().default(''),
    lastName:      z.string().default(''),
    phone:         z.string().default(''),
    // The SMS number, kept apart from `phone` because a text to a landline
    // dies silently. Always optional: no cell ⇒ no texting, stated outright.
    // A number that IS given must be textable (same rule as the server's
    // E.164 gate — see lib/phone).
    cellPhone:     z.string().default('')
      .refine(v => isTextablePhone(v), { message: t('validation.phoneE164') }),
    companyName:   z.string().default(''),
    companyPhone:  z.string().default(''),
    companyEmail:  z.string().default(''),
  })
}

// The address row (design 170): a civic number and a postal code, the street and
// town behind « Sans code postal ». A blank address is still allowed — a
// dispatcher who lacks it must still capture the call — but a started one must
// be askable: a number, and a full postal code or a street with a town. Less
// than that pins the wrong street in the wrong town, so it is not asked.
export function buildNewAddressSchema(t: TFn) {
  return z.object({
    civicNumber:   z.string().default(''),
    postalCode:    z.string().default(''),
    street:        z.string().default(''),
    city:          z.string().default(''),
  }).superRefine((val, ctx) => refineAddress(val, ctx, t))
}

// `requireNumber`: intake and depots name a house or nothing. The edit forms
// pass false — a row stored before the registry (« Rue Langelier », no number)
// has nothing to ask about, and a name-only edit on it must still save.
export function refineAddress(address: NewAddressValues, ctx: Ctx, t: TFn, { requireNumber = true } = {}) {
  const civicNumber = address.civicNumber.trim()
  const postalCode = address.postalCode.trim()
  const street = address.street.trim()
  const city = address.city.trim()
  if (!civicNumber && !postalCode && !street && !city) return

  if (!civicNumber) {
    if (requireNumber) {
      ctx.addIssue({ code: 'custom', path: ['civicNumber'], message: t('addressLookup.validation.civicNumberRequired') })
    }
    return
  }
  if (postalCode && !isCompletePostalCode(postalCode)) {
    ctx.addIssue({ code: 'custom', path: ['postalCode'], message: t('addressLookup.validation.postalIncomplete') })
  }
  if (!postalCode && !(street && city)) {
    ctx.addIssue({ code: 'custom', path: [street ? 'city' : 'postalCode'], message: t('addressLookup.validation.cityOrPostalRequired') })
  }
}

export function refineInlineCustomer(
  customer: NewCustomerValues,
  ctx: Ctx,
  t: TFn,
) {
  if (customer.kind === 'company') {
    if (!customer.companyName.trim()) {
      ctx.addIssue({
        code: 'custom',
        path: ['newCustomer', 'companyName'],
        message: t('validation.required', { field: t('companies.fields.companyName') }),
      })
    }
    // ⭐ THE PARTIAL-PERSON RULE. The personne-ressource is all-or-nothing:
    // `Contact` validates both names server-side, so a dispatcher who typed
    // only a number would otherwise eat a 422 AFTER the company was already
    // created. Touching any person field therefore requires the two names —
    // and leaving the whole group empty stays perfectly valid.
    const touched = [customer.firstName, customer.lastName, customer.phone, customer.cellPhone]
      .some(v => v.trim())
    if (touched) requireNames(customer, ctx, t)
    return
  }

  // The `contact` branch: the person IS the customer, so the names are the
  // record's own requirement, not a rule about a sub-block.
  requireNames(customer, ctx, t)
}

type PersonFields = { firstName: string; lastName: string }
type Ctx = { addIssue: (issue: { code: 'custom'; path: (string | number)[]; message: string }) => void }

function requireNames(person: PersonFields, ctx: Ctx, t: TFn) {
  if (!person.firstName.trim()) {
    ctx.addIssue({
      code: 'custom',
      path: ['newCustomer', 'firstName'],
      message: t('validation.required', { field: t('contacts.fields.firstName') }),
    })
  }
  if (!person.lastName.trim()) {
    ctx.addIssue({
      code: 'custom',
      path: ['newCustomer', 'lastName'],
      message: t('validation.required', { field: t('contacts.fields.lastName') }),
    })
  }
}

export type NewCustomerValues = {
  kind: 'contact' | 'company'
  firstName: string
  lastName: string
  phone: string
  cellPhone: string
  companyName: string
  companyPhone: string
  companyEmail: string
}

export type NewAddressValues = {
  civicNumber: string
  postalCode: string
  street: string
  city: string
}

export type JobFormValues = {
  serviceLocationId: string
  templateId: string
  intakeLane: 'lead' | 'evaluation' | 'quote' | 'won'
  agreementMode?: 'template' | 'priced' | 'no_proposal'
  agreedAmount?: number
  agreedLabel: string
  agreedTaxable: boolean
  agreedKind: 'material' | 'labor' | 'fee'
  agreedDescription: string
  newCustomer?: NewCustomerValues
  newAddress?: NewAddressValues
}
